// R115 calendar hub — OPS stats: KPI cards that double as the ONE active filter
// (presets.ts picks ≤6 per view). A stat whose layer could not be read shows
// "—" — never 0.
import { registerStat, type StatValue, type ChipSpec } from '../registry';
import type { Property, Stay } from '../types';
import {
  unassignedCleans, atRiskCleans, cleansDay, inspUnassigned, tasksDue, isNegativeInHouse, isAtRiskArrival, within48h,
  isAwaitingReview, isRecoveryOpen, healthTotals, openIssuesOf, UNASSIGNED_HORIZON,
} from '../../../../../lib/calendarOps.mjs';
import { insp, tasks, health, viewIds, findJob } from './util';
import type { CalendarCtx } from '../registry';

const turnsOf = (ctx: CalendarCtx) => (ctx.data.events && ctx.data.events.turns) || [];
const has = <T>(list: T[], pred: (x: T) => boolean) => list.some(pred);
const num = (n: number, tone?: 'good' | 'warn' | 'bad', sub?: string): StatValue => ({ value: n, ...(tone ? { tone } : {}), ...(sub ? { sub } : {}) });
// R115 review: the today-relative stats need TODAY in the loaded days — paged past it (› / Jump to date) they read '—'
// and stop filtering instead of a false 0 / green (the R112 §3 lesson)
const todayOut = (ctx: CalendarCtx) => !!ctx.from && !!ctx.to && (ctx.asOf < ctx.from || ctx.asOf > ctx.to);
const NOT_LOADED: StatValue = { value: '—', sub: "today isn't in the loaded days — tap Today" };

registerStat({
  key: 'cleans_today', label: 'Cleans today', icon: '🧹',
  compute: (ctx) => {
    if (todayOut(ctx)) return NOT_LOADED;
    const c = cleansDay(turnsOf(ctx), ctx.asOf);
    return { value: c.total ? `${c.done}/${c.total}` : 0, tone: c.at_risk ? 'bad' : (c.total && c.done === c.total ? 'good' : undefined), sub: `${c.same_day} same-day · ${c.at_risk} at risk` };
  },
  match: { rows: (p, ctx) => todayOut(ctx) || has(turnsOf(ctx), t => t.listing_id === p.listing_id && t.date === ctx.asOf) },
});
registerStat({
  key: 'unassigned_cleans_7d', label: 'Unassigned cleans', icon: '🧹',
  compute: (ctx) => { if (todayOut(ctx)) return NOT_LOADED; const n = unassignedCleans(turnsOf(ctx), ctx.asOf).length; return num(n, n ? 'bad' : 'good', `next ${UNASSIGNED_HORIZON} days`); },
  match: { rows: (p, ctx) => todayOut(ctx) || unassignedCleans(turnsOf(ctx), ctx.asOf).some(t => t.listing_id === p.listing_id) },
});
registerStat({
  key: 'at_risk_same_day', label: 'At-risk same-day', icon: '⏱',
  compute: (ctx) => { if (todayOut(ctx)) return NOT_LOADED; const n = atRiskCleans(turnsOf(ctx), ctx.asOf).length; return num(n, n ? 'bad' : 'good', "today's same-day cleans not done"); },
  match: { rows: (p, ctx) => todayOut(ctx) || atRiskCleans(turnsOf(ctx), ctx.asOf).some(t => t.listing_id === p.listing_id) },
});
registerStat({
  key: 'inspections_unassigned', label: 'Inspections to assign', icon: '🔍', layer: 'inspections',
  compute: (ctx) => {
    const L = insp(ctx);
    if (!L) return { value: null, sub: 'inspections unavailable' };
    if (todayOut(ctx)) return NOT_LOADED;
    const ids = new Set(viewIds(ctx));
    const list = inspUnassigned(L.jobs, ctx.asOf).filter(j => ids.has(j.listing_id));
    const missed = list.filter(j => j.missed).length;
    return num(list.length, list.length ? 'warn' : 'good', `next ${UNASSIGNED_HORIZON} days${missed ? ` · ${missed} missed` : ''}`);
  },
  match: {
    rows: (p, ctx) => todayOut(ctx) || (() => { const L = insp(ctx); return !!L && inspUnassigned(L.jobs, ctx.asOf).some(j => j.listing_id === p.listing_id); })(),
    chips: (c: ChipSpec, ctx) => { if (c.entity.kind !== 'inspection') return false; const j = findJob(ctx, c.entity.id); return !!j && j.state === 'unassigned'; },
  },
});
registerStat({
  key: 'tasks_due_today', label: 'Tasks due', icon: '✓', layer: 'tasks',
  compute: (ctx) => {
    const L = tasks(ctx);
    if (!L) return { value: null, sub: 'tasks unavailable' };
    if (todayOut(ctx)) return NOT_LOADED;
    const due = tasksDue(L.items, ctx.asOf);
    const overdue = due.filter(t => t.overdue).length;
    return num(due.length, overdue ? 'bad' : (due.length ? 'warn' : 'good'), `${overdue} overdue · ${due.filter(t => !t.listing_id).length} office`);
  },
  match: {
    rows: (p, ctx) => todayOut(ctx) || (() => { const L = tasks(ctx); return !!L && tasksDue(L.items, ctx.asOf).some(t => t.listing_id === p.listing_id); })(),
    chips: (c: ChipSpec, ctx) => c.entity.kind === 'task' && c.start <= ctx.asOf,
  },
});
// ── guests (base payload) ────────────────────────────────────────────────
const staysIn = (ctx: CalendarCtx, pred: (s: Stay) => boolean) => ctx.data.stays.filter(pred);
registerStat({
  key: 'negative_in_house', label: 'Negative in house', icon: '😟',
  compute: (ctx) => { const n = staysIn(ctx, isNegativeInHouse).length; return num(n, n ? 'bad' : 'good', 'open, negative guest thread'); },
  match: { stays: (s) => isNegativeInHouse(s) },
});
registerStat({
  key: 'at_risk_arrivals', label: 'At-risk arrivals', icon: '🚪',
  compute: (ctx) => {
    const list = staysIn(ctx, s => isAtRiskArrival(s, ctx.asOf));
    return num(list.length, list.length ? 'bad' : 'good', `next 7 days · ${list.filter(within48h).length} within 48h`);
  },
  match: {
    stays: (s, ctx) => isAtRiskArrival(s, ctx.asOf),
    rows: (p: Property, ctx) => ctx.staysOf(p.listing_id).some(s => isAtRiskArrival(s, ctx.asOf)),
  },
});
registerStat({
  key: 'awaiting_review', label: 'Awaiting review', icon: '☆',
  compute: (ctx) => num(staysIn(ctx, isAwaitingReview).length, undefined, 'checked out, no review yet'),
  match: { stays: (s) => isAwaitingReview(s) },
});
registerStat({
  key: 'recovery_open', label: 'Recovery open', icon: '🛟',
  compute: (ctx) => { const n = staysIn(ctx, isRecoveryOpen).length; return num(n, n ? 'bad' : 'good', 'stays in view'); },
  match: { stays: (s) => isRecoveryOpen(s) },
});
// ── health (health layer) ────────────────────────────────────────────────
const tot = (ctx: CalendarCtx) => { const L = health(ctx); return L ? healthTotals(L.by_listing, viewIds(ctx)) : null; };
registerStat({
  key: 'open_issues', label: 'Open issues · 30d', icon: '⚑', layer: 'health',
  compute: (ctx) => {
    const t = tot(ctx);
    if (!t || t.open == null) return { value: null, sub: 'issues unavailable' };
    return num(t.open, t.hot ? 'bad' : (t.open ? 'warn' : 'good'), `${t.open_excl_messages} excl. guest msgs · ${t.hot} hot`);
  },
  match: { rows: (p, ctx) => { const L = health(ctx); return !!L && (openIssuesOf(L.by_listing[p.listing_id]) || 0) > 0; } },
});
registerStat({
  key: 'avg_score', label: 'Airbnb ★', icon: '★', layer: 'health',
  compute: (ctx) => {
    const t = tot(ctx);
    if (!t) return { value: null, sub: 'scores unavailable' };
    if (t.avg_score == null) return { value: null, sub: 'no rated Airbnb reviews' };
    const L = health(ctx)!;
    const below = viewIds(ctx).filter(id => { const r = L.by_listing[id]; return !!r && r.score != null && r.score < t.avg_score!; }).length;
    return { value: t.avg_score.toFixed(2), tone: t.avg_score >= 4.8 ? 'good' : t.avg_score < 4.6 ? 'bad' : 'warn', sub: `${t.score_n} reviews · ${below} homes below` };
  },
  match: {
    rows: (p, ctx) => {
      const L = health(ctx); const t = tot(ctx);
      if (!L || !t || t.avg_score == null) return false;
      const r = L.by_listing[p.listing_id];
      return !!r && r.score != null && r.score < t.avg_score;
    },
  },
});
registerStat({
  key: 'reviews_30d', label: 'Reviews · 30d', icon: '💬', layer: 'health',
  compute: (ctx) => {
    const t = tot(ctx);
    if (!t) return { value: null, sub: 'reviews unavailable' };
    return num(t.reviews_30d, undefined, t.reviews_30d_avg != null ? `avg ${t.reviews_30d_avg.toFixed(2)}★` : 'no rated reviews');
  },
  match: { rows: (p, ctx) => { const L = health(ctx); const r = L && L.by_listing[p.listing_id]; return !!r && (r.reviews_30d || 0) > 0; } },
});
